// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Workspace, ViewState } from '@lfx/key-projects/key-projects.model';
import { ButtonComponent } from '@lfx/shared/components/button';
import { environment } from '@environment';

@Component({
  selector: 'lfx-workspace-dropdown',
  imports: [CommonModule, NgbDropdownModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="workspace-dropdown" ngbDropdown #dropdown="ngbDropdown">
      <!-- Dropdown Trigger -->
      <button
        class="workspace-dropdown-toggle"
        ngbDropdownToggle
        [attr.aria-label]="dropdownLabel()"
      >
        <span class="dropdown-label">
          @if (currentWorkspaceName()) {
            <span class="label-prefix">Workspace:</span>
            <span class="label-value">{{ currentWorkspaceName() }}</span>
          }
        </span>
        <i class="fal fa-chevron-down dropdown-icon"></i>
      </button>

      <!-- Dropdown Menu -->
      <div class="lf-dropdown-menu workspace-dropdown-menu" ngbDropdownMenu>
        <div class="lf-dropdown-content">
          <!-- Workspaces Header -->
          <h6 class="lf-dropdown-header">Shared Workspaces</h6>

          <!-- Workspaces Section -->
          <div class="lf-dropdown-section workspaces-section">
            <!-- Workspace Items -->
            @for (workspace of workspaces(); track workspace.id) {
              <button
                class="lf-dropdown-item workspace-item"
                [class.active]="selectedWorkspaceId() === workspace.id"
                (click)="onWorkspaceClick(workspace)"
                ngbDropdownItem
              >
                <span class="item-text">{{ workspace.name }}</span>
                @if (selectedWorkspaceId() === workspace.id) {
                  <i class="fal fa-check check-icon"></i>
                }
              </button>
            } @empty {
              <!-- No Workspaces Message -->
              <div class="dropdown-item-text text-muted">
                No workspaces available
              </div>
            }
          </div>

          <!-- Divider -->
          <div class="lf-dropdown-divider"></div>

          <!-- Add Workspace Action -->
          <lfx-button
            icon="far fa-folder-plus"
            variant="ghost-accent"
            (click)="onAddWorkspaceClick()"
          >
            Add workspace
          </lfx-button>

          <!-- Rebuild All Workspaces (DEV/LOCAL only) -->
          <!-- @if (!isProduction) {
            <div class="lf-dropdown-divider"></div>
            <lfx-button
              icon="far fa-sync-alt"
              variant="ghost"
              (click)="onRebuildAllWorkspacesClick()"
            >
              Rebuild all workspaces
            </lfx-button>
          } -->
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./workspace-dropdown.component.scss'],
})
export class WorkspaceDropdownComponent {
  readonly workspaces = input<Workspace[]>([]);
  readonly viewState = input<ViewState | null>(null);

  readonly workspaceSelected = output<string>();
  readonly addWorkspaceClicked = output<void>();
  readonly rebuildAllWorkspacesClicked = output<void>();

  readonly dropdown = viewChild<NgbDropdown>('dropdown');

  // Environment check for dev-only features
  protected readonly isProduction = environment.production;

  // Computed signals - memoized for performance
  protected readonly selectedWorkspaceId = computed(() => {
    const state = this.viewState();
    return state?.mode === 'workspace-projects' ? state.selectedWorkspaceId : null;
  });

  protected readonly currentWorkspace = computed(() => {
    const id = this.selectedWorkspaceId();
    return id ? this.workspaces().find(ws => ws.id === id) ?? null : null;
  });

  protected readonly currentWorkspaceName = computed(() => this.currentWorkspace()?.name);

  protected readonly dropdownLabel = computed(() => {
    const ws = this.currentWorkspace();
    return ws ? `Workspace: ${ws.name}` : 'Select workspace';
  });

  // Event handlers
  protected onWorkspaceClick(workspace: Workspace): void {
    this.workspaceSelected.emit(workspace.id);
  }

  protected onAddWorkspaceClick(): void {
    this.addWorkspaceClicked.emit();
    this.dropdown()?.close();
  }

  protected onRebuildAllWorkspacesClick(): void {
    this.rebuildAllWorkspacesClicked.emit();
    this.dropdown()?.close();
  }
}
